import type { FlowTemplateSchema } from "@/app/_api/flowTemplatesServer";
import { appUrls } from "@/constants";

import { buildFlowInputs } from "./runFlowAndAwaitResult";

// The xi web-app deep link both builders below start from: the template's run
// page on the app origin. They then layer on their own query params.
function buildTemplateBaseUrl(flowTemplate: FlowTemplateSchema) {
  return new URL(`/app/templates/${encodeURIComponent(flowTemplate.id)}`, appUrls.root);
}

/**
 * Build the xi web-app deep link used as the "sign up to keep going" CTA when an
 * anonymous flow run is rate-limited / quota-exceeded, or for video templates
 * (which always require an account). Seeds the template's input ports from the
 * already-uploaded asset ids and inline texts so the run is one click away once
 * the visitor signs in.
 */
export function buildFlowTemplateRedirectUrl({
  flowTemplate,
  assetIdsByPort,
  textsByPort,
}: {
  flowTemplate: FlowTemplateSchema;
  assetIdsByPort: Record<string, string>;
  textsByPort?: Record<string, string>;
}): string {
  const url = buildTemplateBaseUrl(flowTemplate);
  // Reuses the run's own port-merge rule (asset wins on a colliding port, texts
  // trimmed and skipped when empty), flattened into `input.<portId>` params
  // instead of xi reference objects.
  for (const [portId, value] of Object.entries(buildFlowInputs({ assetIdsByPort, textsByPort }))) {
    if ("content_asset_id" in value) {
      url.searchParams.set(`input.${portId}`, value.content_asset_id);
    } else if ("inline_text" in value) {
      url.searchParams.set(`input.${portId}`, value.inline_text);
    }
  }
  return url.toString();
}

/**
 * Build the xi web-app deep link used as the post-result CTA. `claim_session_id`
 * lets the authenticated app re-attach the anonymous run's generation session so
 * the visitor can claim the result they just produced.
 */
export function buildFlowTemplateClaimUrl({
  flowTemplate,
  sessionId,
}: {
  flowTemplate: FlowTemplateSchema;
  sessionId: string;
}): string {
  const url = buildTemplateBaseUrl(flowTemplate);
  url.searchParams.set("claim_session_id", sessionId);
  return url.toString();
}
